import React, { useState } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";

import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  toolbar: {
    backgroundColor: "#1C1E20",
    border: "2px solid rgba(232, 163, 61, 0.3)",
    borderRadius: "12px",
    padding: "1rem 1.5rem",
    marginBottom: "2rem",
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    flexWrap: "wrap",
  },
  selectedText: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1rem",
  },
  actionButton: {
    fontWeight: 700,
  },
  validateButton: {
    backgroundColor: "#00C853",
    "&:hover": {
      backgroundColor: "#00E676",
    },
  },
  dismissButton: {
    backgroundColor: "#9E9E9E",
    "&:hover": {
      backgroundColor: "#BDBDBD",
    },
  },
  falseReportButton: {
    backgroundColor: "#FF9800",
    "&:hover": {
      backgroundColor: "#FFB74D",
    },
  },
});

function BulkActionToolbar({ classes, selectedCount, selectedReports, userId, onComplete }) {
  const [processing, setProcessing] = useState(false);

  const handleBulkAction = async (decision) => {
    if (processing) return;

    const confirmed = window.confirm(
      `Are you sure you want to ${decision} ${selectedCount} report(s)?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setProcessing(true);

    try {
      // Process each report sequentially
      let successCount = 0;
      let errorCount = 0;

      for (const reportId of selectedReports) {
        try {
          const response = await fetch(
            `${basePath}/api/v1/admin/reputation/review-report/adpass/${userId}`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                reportId,
                decision,
                adminNotes: `Bulk action: ${decision}`,
              }),
            }
          );

          const result = await response.json();
          if (result.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (err) {
          console.error(`Error processing report ${reportId}:`, err);
          errorCount++;
        }
      }

      alert(
        `Bulk action complete:\n\n✓ Success: ${successCount}\n✗ Errors: ${errorCount}`
      );

      onComplete();
    } catch (err) {
      console.error("Error during bulk action:", err);
      alert("Error processing bulk action");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Paper className={classes.toolbar}>
      <Typography className={classes.selectedText}>
        {selectedCount} report{selectedCount !== 1 ? "s" : ""} selected
      </Typography>

      <Button
        variant="contained"
        className={`${classes.actionButton} ${classes.validateButton}`}
        startIcon={processing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        onClick={() => handleBulkAction("VALIDATED")}
        disabled={processing}
      >
        Validate Selected ({selectedCount})
      </Button>

      <Button
        variant="contained"
        className={`${classes.actionButton} ${classes.dismissButton}`}
        startIcon={processing ? <CircularProgress size={20} /> : <CancelIcon />}
        onClick={() => handleBulkAction("DISMISSED")}
        disabled={processing}
      >
        Dismiss Selected ({selectedCount})
      </Button>

      <Button
        variant="contained"
        className={`${classes.actionButton} ${classes.falseReportButton}`}
        startIcon={processing ? <CircularProgress size={20} /> : <WarningIcon />}
        onClick={() => handleBulkAction("FALSE_REPORT")}
        disabled={processing}
      >
        Mark as False Reports ({selectedCount})
      </Button>
    </Paper>
  );
}

BulkActionToolbar.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedCount: PropTypes.number.isRequired,
  selectedReports: PropTypes.array.isRequired,
  userId: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default withStyles(styles)(BulkActionToolbar);
